import { useQuery } from '@tanstack/react-query'
import { LumpsumRecommendationActionType } from '../../../api/common.types'
import { fetchLumpsumRebalancePlanRecommendation } from '../../api/repository'
import { LUMPSUM_PLANS_API_RETRY_COUNT, isLumpsumQueryFetchFailed, isLumpsumQueryLoading } from '../../../api/queryOptions'
import type {
  LumpsumRebalancePlanRecommendationApiResponse,
  LumpsumRebalanceSellPlanRecommendationDetails
} from '../../api/rebalanceRecommendation.types'
import type { FundMissedGainsInfoParams } from '../../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { RebalanceSellPlanRecommendationSheet } from './RebalanceSellPlanRecommendationSheet'

const REBALANCE_SELL_PLAN_RECOMMENDATION_QUERY_KEY = 'lumpsum-plans-v4-rebalance-sell-recommendation'

export function RebalanceSellPlanRecommendationContainer(input: {
  onDismiss: () => void
  isin: string
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
  transactionCta: unknown
}) {
  const { onDismiss, isin, onMissedGainsInfoClick, transactionCta } = input
  const { planDetails, isLoading, isFetchFailed, onRetry } = useRebalanceSellPlanRecommendation(isin, true)

  return (
    <RebalanceSellPlanRecommendationSheet
      onDismiss={onDismiss}
      isin={isin}
      planDetails={planDetails}
      isLoading={isLoading}
      isFetchFailed={isFetchFailed}
      onRetry={onRetry}
      onMissedGainsInfoClick={onMissedGainsInfoClick}
      transactionCta={transactionCta}
    />
  )
}

function useRebalanceSellPlanRecommendation(isin: string, enabled: boolean): {
  planDetails: LumpsumRebalanceSellPlanRecommendationDetails | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
} {
  const query = useQuery({
    queryKey: [REBALANCE_SELL_PLAN_RECOMMENDATION_QUERY_KEY, isin],
    queryFn: () =>
      fetchLumpsumRebalancePlanRecommendation(isin, LumpsumRecommendationActionType.SELL),
    enabled: enabled && isin.length > 0,
    retry: LUMPSUM_PLANS_API_RETRY_COUNT
  })
  const response = query.data ?? null

  return {
    planDetails: isSellPlanRecommendationDetails(response) ? response : null,
    isLoading: isLumpsumQueryLoading(query),
    isFetchFailed: isLumpsumQueryFetchFailed(query),
    onRetry: () => {
      void query.refetch()
    }
  }
}

function isSellPlanRecommendationDetails(
  response: LumpsumRebalancePlanRecommendationApiResponse | null
): response is LumpsumRebalanceSellPlanRecommendationDetails {
  return response != null && 'fundsToBeBought' in response
}
